import { LocalStorageAPI } from './api-types';
import { getMetadataDictionaryKey, getPresentationIdAndSlideId } from '../shared/helpers';
import { SlideType, Metadata } from '../shared/types';
import { extractParams } from '../../routes/helpers';
import routes from '../../routes/router';
import { enumFromStringValue } from '../../utils/string';
import { PollSlideTypes } from '../types';

export function transformSaveLocalDataPayload(
  presentationId: string,
  slideId: string,
  slideMetadata: Metadata
): LocalStorageAPI.SaveLocalDataTransformedRequest | undefined {
  if (slideMetadata.slideType === SlideType.RESTRICTED) return;
  if (slideMetadata.slideType === SlideType.HOME) return;

  let transformedPayload: LocalStorageAPI.SaveLocalDataTransformedRequest | undefined = undefined;

  const commonPayload: LocalStorageAPI.CommonSlideData = {
    presentationId,
    slideId,
  };

  if (slideMetadata.slideType === SlideType.TIMER) {
    transformedPayload = {
      ...commonPayload,
      slideType: slideMetadata.slideType,
      details: {
        title: slideMetadata.timer?.title ?? '',
        bgImg: slideMetadata.timer?.bgImg ?? '',
        countdownFrom: slideMetadata.timer?.countdownFrom ?? 0,
        ranTill: 0,
      },
    };
  }

  if (slideMetadata.slideType === SlideType.ATTENDANCE) {
    const extractedParams = extractParams(routes.attendance.path, slideMetadata.url);
    if (!extractedParams) return;

    transformedPayload = {
      ...commonPayload,
      slideType: slideMetadata.slideType,
      details: {
        activityType: 'attendance',
      },
    };
  }

  if (slideMetadata.slideType === SlideType.BAR_GRAPH || slideMetadata.slideType === SlideType.PIE_CHART) {
    const extractedParams = extractParams(routes.poll.path, slideMetadata.url);
    if (!extractedParams) return;

    const { universitySlug, courseId, classId, pollId } = extractedParams;

    transformedPayload = {
      ...commonPayload,
      slideType: slideMetadata.slideType,
      details: {
        university: universitySlug,
        courseId,
        classId,
        activityId: pollId,
        activityType: 'polls',
      },
    };
  }

  return transformedPayload;
}

export function transformGetLocalDataResponse(response: LocalStorageAPI.GetLocalDataResponse) {
  if (response.message !== 'success') return;
  const { slides, presentationId } = response;

  const metadataDictionary: Record<string, Metadata> = slides.reduce(
    (acc, slide) => {
      const { slideId, slideType } = slide;
      const metadataId = getMetadataDictionaryKey(presentationId, slideId);

      if (slideType === SlideType.RESTRICTED) {
        acc[metadataId] = {
          url: routes.restrictedPage.url(),
          slideType,
        };
        return acc;
      }

      if (slideType === SlideType.BAR_GRAPH || slideType === SlideType.PIE_CHART) {
        const { details } = slide;
        const { university, courseId, classId, activityId } = details;

        const pollSlideType = enumFromStringValue(PollSlideTypes, slideType);
        if (!pollSlideType) return acc;

        acc[metadataId] = {
          url: routes.poll.url({
            universitySlug: university,
            courseId,
            classId,
            pollId: activityId,
            slideType: pollSlideType,
          }),
          slideType,
        };
        return acc;
      }

      if (slideType === SlideType.ATTENDANCE) {
        acc[metadataId] = {
          url: routes.attendance.url(),
          slideType,
        };
        return acc;
      }

      if (slideType === SlideType.TIMER) {
        const { details } = slide;
        const { title, bgImg, countdownFrom, ranTill } = details;
        acc[metadataId] = {
          url: routes.timer.path,
          slideType,
          timer: {
            title,
            bgImg,
            countdownFrom,
            ranTill,
          },
        };
        return acc;
      }

      acc[metadataId] = {
        url: '',
        slideType,
      };

      return acc;
    },
    {} as Record<string, Metadata>
  );
  return metadataDictionary;
}
